import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { ESSAP_CATS, catLabel } from "@/lib/essap";
import { Button } from "@/components/ui/button";
import { EssapPostDialog } from "@/components/admin/EssapPostDialog";
import { ConfirmDelete } from "@/components/admin/ConfirmDelete";

const Chip = ({ active, onClick, children, testId }) => (
  <button onClick={onClick} data-testid={testId} className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap border transition-colors ${active ? "bg-emerald-700 text-white border-emerald-700" : "bg-white text-slate-600 border-emerald-900/10 hover:border-emerald-700/40"}`}>{children}</button>
);

export default function EssapPosts() {
  const [cat, setCat] = useState("");
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState({ open: false, post: null });
  const [del, setDel] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/essap/posts", { params: { categorie: cat } }).then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [cat]);
  useEffect(() => { load(); }, [load]);

  const remove = async () => {
    try { await api.delete(`/admin/essap/posts/${del.id}`); toast.success("Publication supprimée"); load(); } catch (e) { toast.error(errMsg(e)); }
    setDel(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Publications ESSAP</h1>
          <p className="text-slate-500 mt-1">Communiqués, étudiants retenus, programmes et résultats d'examens.</p>
        </div>
        <Button onClick={() => setEdit({ open: true, post: null })} data-testid="essap-post-add-button" className="rounded-full"><Plus className="h-4 w-4 mr-1" /> Nouvelle publication</Button>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1">
        <Chip active={!cat} onClick={() => setCat("")} testId="essap-filter-all">Toutes</Chip>
        {ESSAP_CATS.map((c) => <Chip key={c.key} active={cat === c.key} onClick={() => setCat(c.key)} testId={`essap-filter-${c.key}`}>{c.label}</Chip>)}
      </div>
      <div className="space-y-3">
        {items.map((p) => (
          <div key={p.id} data-testid={`admin-essap-post-${p.id}`} className="flex items-center gap-4 rounded-2xl border border-emerald-900/10 bg-white p-4">
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-slate-900 truncate">{p.titre}</div>
              <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-2 items-center">
                <span className="rounded-full px-2 py-0.5 bg-emerald-50 text-emerald-800 border border-emerald-200">{catLabel(p.categorie)}</span>
                <span>{fmtDate(p.created_at)}</span>
                <span className={`rounded-full px-2 py-0.5 border ${p.publie ? "border-emerald-300 text-emerald-800" : "border-slate-300 text-slate-600"}`}>{p.publie ? "Publié" : "Brouillon"}</span>
                {p.fichier && <span className="inline-flex items-center gap-1 text-rose-700"><FileText className="h-3.5 w-3.5" /> PDF</span>}
              </div>
            </div>
            <Button size="icon" variant="ghost" onClick={() => setEdit({ open: true, post: p })} data-testid={`essap-post-edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>
            <Button size="icon" variant="ghost" onClick={() => setDel(p)} data-testid={`essap-post-delete-${p.id}`} className="text-rose-600"><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
        {!items.length && <p data-testid="admin-essap-posts-empty" className="text-slate-500 py-8">Aucune publication.</p>}
      </div>
      <EssapPostDialog open={edit.open} post={edit.post} defaultCat={cat} onOpenChange={(o) => setEdit((s) => ({ ...s, open: o }))} onSaved={load} />
      <ConfirmDelete open={!!del} onOpenChange={(o) => !o && setDel(null)} onConfirm={remove} title="Supprimer cette publication ?" description={del?.titre || ""} />
    </div>
  );
}
